import BoltIcon from "@mui/icons-material/Bolt";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FilterListIcon from "@mui/icons-material/FilterList";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputBase from "@mui/material/InputBase";
import LinearProgress from "@mui/material/LinearProgress";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Dropdown from "../../components/Dropdown/Dropdown";
import DynamicNestedTable from "../../components/DynamicTable/DynamicNestedTable";
import ExcelSheetTabs from "../../components/ExcelSheetTabs/ExcelSheetTabs";
import Modal from "../../components/Modal/Modal";
import SqlEditor from "../../components/SqlEditor/SqlEditor";
import AddColumnModal from "../../components/AddColumnModal/AddColumnModal";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { AGG_FUNCTIONS, FILTER_CONDITIONS } from "../../enum/enums";
import { StorageHelper } from "../../utils/storageHelper";
import { useLandingPageModel } from "../../viewmodels/LandingPageModel";
import {
  DropdownOption,
  DropdownSearchInput,
  DropItemCard,
  DropLabel,
  DropText,
  DropZone,
  RadioIndicator,
  SelectionIndicator,
  ZoneContainer,
  DropIndicator,
} from "./LandingPage.styled";
import { CircularProgress, Tooltip } from "@mui/material";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

export default function LandingPage() {
  const QUERY_NAME_MAX_LEN = 50;
  const QUERY_NAME_ALLOWED_RE = /^[A-Za-z0-9_]*$/;

  const location = useLocation();
  const navigate = useNavigate();
  const model = useLandingPageModel();

  // Destructure all needed state and handlers from the model
  const {
    columns,
    setColumns,
    rows,
    setRows,
    data,
    setData,
    dragOver,
    setDragOver,
    tableLoading,
    setTableLoading,
    tableData,
    setTableData,
    expandedMap,
    setExpandedMap,
    uploadedFlat,
    setUploadedFlat,
    uploadedHeaders,
    setUploadedHeaders,
    uploadDialogOpen,
    setUploadDialogOpen,
    columnTypes,
    setColumnTypes,
    uploadedFiles,
    setUploadedFiles,
    dataFilters,
    setDataFilters,
    filterDropdownOpen,
    setFilterDropdownOpen,
    filterMenuAnchor,
    setFilterMenuAnchor,
    filterMenuCol,
    setFilterMenuCol,
    filterSearch,
    setFilterSearch,
    filterOptions,
    setFilterOptions,
    filterLoading,
    setFilterLoading,
    filterError,
    setFilterError,
    filterCondition,
    setFilterCondition,
    pendingFilter,
    setPendingFilter,
    pendingValuesByCondition,
    setPendingValuesByCondition,
    queryResultTableData,
    setQueryResultTableData,
    generatedSqlQuery,
    setGeneratedSqlQuery,
    aggregations,
    setAggregations,
    aggDropdownCol,
    setAggDropdownCol,
    aggDropdownValue,
    setAggDropdownValue,
    filterIconRefs,
    uploadFileToMinio,
    uploadLoading,
    uploadError,
    uploadData,
    createQueryApi,
    sqlLoading,
    sqlError,
    sqlData,
    getRecordsByColumnNameApi,
    getRecordsLoading,
    getRecordsError,
    getRecordsData,
    runQueryApi,
    runQueryLoading,
    runQueryError,
    runQueryData,
    schemaInfo,
    user,
    userId,
    formatNumber,
    flattenData,
    getAllColumns,
    getValue,
    getCurrentDbType,
    handleDrop,
    handleFileChange,
    handleGenerateSQL,
    executeCustomSql,
    filters,
    setFilters,
    sqlGenError,
    setSqlGenError,
    saveQueryDialogOpen,
    setSaveQueryDialogOpen,
    queryName,
    setQueryName,
    saveQueryError,
    setSaveQueryError,
    saveQuerySuccess,
    setSaveQuerySuccess,
    handleSaveQuery,
    saveQuery,
    closeSaveQueryDialog,
    modal,
    setModal,
    // Sheet management
    sheets,
    setSheets,
    activeSheetIndex,
    setActiveSheetIndex,
    addSheet,
    addSheetWithData,
    deleteSheet,
    renameSheet,
    duplicateSheet,
    reorderSheets,
    switchToSheet,
    saveCurrentSheetData,
    addSavedQueryData,
    addSavedQueryLoading,
    updateSavedQueryLoading,
    editSaveSuccessAlert,
    clearQueryData,
    downloadQuery,
    downloadQueryAsync,
    getColumnDateDetectionApi,
    getColumnDateDetectionLoading,
    getChartDataApi,
    getChartDataLoading,
    getChartDataError,
    // Computed columns
    computedColumns,
    addComputedColumn,
    removeComputedColumn,
    updateComputedColumn,
    getTableDataWithComputedColumns,
    // Failed calculated column tracking
    failedCalculatedColumn,
    setFailedCalculatedColumn,
  } = model;

  // When navigating from file upload success, always open a new Task tab
  // instead of reusing the currently active one.
  useEffect(() => {
    if (location?.state?.fromUpload) {
      addSheet(0);
      setQueryData(null);
      navigate("/dashboard", { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Disable Run Query when no selections exist in Columns/Rows/Data/Filters
  const hasQuerySelections =
    (columns?.length || 0) > 0 ||
    (rows?.length || 0) > 0 ||
    (data?.length || 0) > 0 ||
    (filters?.length || 0) > 0;

  const [queryData, setQueryData] = useState(location.state?.query);
  const [addColumnModalOpen, setAddColumnModalOpen] = useState(false);
  const [editColumnName, setEditColumnName] = useState(null);
  const [editFormula, setEditFormula] = useState(null);
  const [queryProcessed, setQueryProcessed] = React.useState(false);
  const savedQueryLoadStartedRef = React.useRef(null);
  const [underlinedDownloadLoading, setUnderlinedDownloadLoading] =
    React.useState(false);
  const [summarizedDownloadLoading, setSummarizedDownloadLoading] =
    React.useState(false);
  const [chartDialogOpen, setChartDialogOpen] = useState(false);
  const [chartPeriod, setChartPeriod] = useState("monthly");
  const [chartYear, setChartYear] = useState(() => new Date().getFullYear());
  const [chartXAxis, setChartXAxis] = useState("");
  const [chartYAxis, setChartYAxis] = useState("");
  const [chartData, setChartData] = useState(null);
  const [chartError, setChartError] = useState(null);
  const [chartType, setChartType] = useState("bar"); // "bar" | "line" | "area"
  const [chartXAxisDateDetection, setChartXAxisDateDetection] = useState(null); // { isDate } from getColumnDateDetection
  const [chartYAxisDateDetection, setChartYAxisDateDetection] = useState(null);

  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const actionsMenuOpen = Boolean(actionsMenuAnchor);
  const handleActionsMenuOpen = (e) => setActionsMenuAnchor(e.currentTarget);
  const handleActionsMenuClose = () => setActionsMenuAnchor(null);
  const actionsMenuContainerRef = React.useRef(null);
  const [dropIndex, setDropIndex] = React.useState({ zone: null, index: null });
  const [filterHasMore, setFilterHasMore] = useState(false);
  const [filterLoadingMore, setFilterLoadingMore] = useState(false);
  // Flag to trigger SQL regeneration after state updates (replaces setTimeout)
  const [shouldRegenerateSQL, setShouldRegenerateSQL] = useState(false);
  // State for collapsible left panel
  const [isLeftPanelExpanded, setIsLeftPanelExpanded] = useState(true);

  // Check if current sheet is using postgres (for Add Column feature)
  const isPostgresSheet = React.useMemo(() => {
    if (
      !sheets ||
      sheets.length === 0 ||
      activeSheetIndex < 0 ||
      activeSheetIndex >= sheets.length
    ) {
      return false;
    }

    const currentSheet = sheets[activeSheetIndex];
    if (!currentSheet?.data) {
      return false;
    }

    const sheetColumns = [
      ...(currentSheet?.data?.columns || []),
      ...(currentSheet?.data?.rows || []),
      ...(currentSheet?.data?.data || []),
    ];

    // If we have columns, check if they're all postgres
    if (sheetColumns?.length > 0) {
      return sheetColumns.every(
        (item) => item.databaseType === "postgres"
      );
    }
    return false;
  }, [sheets, activeSheetIndex]);

  // Table column names for chart X/Y axis dropdowns (from query result)
  const tableHeaders = React.useMemo(() => {
    if (
      !queryResultTableData ||
      !Array.isArray(queryResultTableData) ||
      queryResultTableData.length === 0
    ) {
      return [];
    }
    return Array.from(
      new Set(queryResultTableData.flatMap((row) => Object.keys(row)))
    );
  }, [queryResultTableData]);

  // Combined columns + rows + data for chart X/Y axis dropdowns
  const chartAxisOptions = React.useMemo(() => {
    const fromColumns = (columns || []).map((c) => c.name).filter(Boolean);
    const fromRows = (rows || []).map((r) => r.name).filter(Boolean);
    const fromData = (data || []).map((d) => d.name).filter(Boolean);
    return Array.from(new Set([...fromColumns, ...fromRows, ...fromData]));
  }, [columns, rows, data]);

  // True when selected X-axis is a date column: from getColumnDateDetection API result, else column type metadata
  const isChartXAxisDateType = React.useMemo(() => {
    if (!chartXAxis) return false;
    if (chartXAxisDateDetection && (chartXAxisDateDetection.columnName || "").trim().toLowerCase() === (chartXAxis || "").trim().toLowerCase()) {
      return chartXAxisDateDetection.isDate === true;
    }
    const all = [...(columns || []), ...(rows || []), ...(data || [])];
    const item = all.find((i) => (i.name || "").trim() === (chartXAxis || "").trim());
    const type = (
      columnTypes?.[chartXAxis] ||
      item?.textDatatype ||
      item?.type ||
      item?.databaseType ||
      ""
    ).toLowerCase();
    return ["date", "timestamp", "datetime", "time", "year", "month"].some((t) => type.includes(t));
  }, [chartXAxis, chartXAxisDateDetection, columns, rows, data, columnTypes]);

  // Year options: from getColumnDateDetection when X-axis is date (distinct years), else fallback range
  const chartYearOptions = React.useMemo(() => {
    if (isChartXAxisDateType && chartXAxisDateDetection?.years?.length > 0) {
      return chartXAxisDateDetection.years;
    }
    const current = new Date().getFullYear();
    return Array.from({ length: 18 }, (_, i) => current - 15 + i);
  }, [isChartXAxisDateType, chartXAxisDateDetection?.years]);

  // When X-axis date detection returns years, default chart year to latest if current not in list
  React.useEffect(() => {
    if (chartXAxisDateDetection?.years?.length > 0 && !chartXAxisDateDetection.years.includes(chartYear)) {
      setChartYear(chartXAxisDateDetection.years[chartXAxisDateDetection.years.length - 1]);
    }
  }, [chartXAxisDateDetection?.years, chartYear]);

  // Fetch date detection for a column when X-axis or Y-axis selection changes (chart dialog context)
  const fetchColumnDateDetection = React.useCallback(
    (columnName, setDetectionState) => {
      if (!columnName || !generatedSqlQuery?.trim() || !userId) {
        setDetectionState(null);
        return;
      }
      let userDbId =
        StorageHelper.get("snowflake_datasource_id") ||
        StorageHelper.get("selected_user_db_id");
      const currentSheet = sheets?.[activeSheetIndex];
      if (currentSheet?.data?.columns?.every((c) => c?.dbname?.startsWith?.("temp_"))) {
        userDbId = "0";
      }
      const dbType = currentSheet?.data?.dbtype || getCurrentDbType() || "snowflake";
      getColumnDateDetectionApi(
        { sql: generatedSqlQuery },
        columnName.trim(),
        Number(userId),
        Number(userDbId),
        dbType
      )
        .then((res) => {
          setDetectionState(
            res.success
              ? { columnName: columnName.trim(), isDate: res.isDate, ...res }
              : null
          );
        })
        .catch(() => setDetectionState(null));
    },
    [
      generatedSqlQuery,
      userId,
      activeSheetIndex,
      sheets,
      getCurrentDbType,
      getColumnDateDetectionApi,
    ]
  );

  // Check if all dbname values are the same across columns, rows, data, and filters
  const { allDbnamesMatch, commonTableName } = React.useMemo(() => {
    if (
      !sheets ||
      sheets.length === 0 ||
      activeSheetIndex < 0 ||
      activeSheetIndex >= sheets.length
    ) {
      return { allDbnamesMatch: false, commonTableName: null };
    }

    const currentSheet = sheets[activeSheetIndex];
    if (!currentSheet?.data) {
      return { allDbnamesMatch: false, commonTableName: null };
    }

    // Collect all dbname values from columns, rows, data, and filters
    const allDbnames = [
      ...(currentSheet.data.columns || []).map((item) => item?.dbname).filter(Boolean),
      ...(currentSheet.data.rows || []).map((item) => item?.dbname).filter(Boolean),
      ...(currentSheet.data.data || []).map((item) => item?.dbname).filter(Boolean),
      ...(currentSheet.data.filters || []).map((item) => item?.dbname).filter(Boolean),
    ];

    // If no dbnames found, return false
    if (allDbnames.length === 0) {
      return { allDbnamesMatch: false, commonTableName: null };
    }

    // Check if all dbnames are the same
    const firstDbname = allDbnames[0];
    const allMatch = allDbnames.every((dbname) => dbname === firstDbname);

    return {
      allDbnamesMatch: allMatch,
      commonTableName: allMatch ? firstDbname : null,
    };
  }, [sheets, activeSheetIndex]);

  // Wrapper function for switchToSheet that clears queryData for non-saved-query tabs
  const handleSwitchToSheet = (sheetIndex) => {
    if (sheetIndex >= 0 && sheetIndex < sheets.length) {
      switchToSheet(sheetIndex);

      // Check if the current tab is from a saved query
      const currentSheet = sheets[sheetIndex];
      if (currentSheet && !currentSheet.saved_query_id) {
        // Clear queryData for new/duplicated tabs so they create new queries instead of updating
        setQueryData(null);
      }
    }
  };

  React.useEffect(() => {
    if (!queryData) {
      savedQueryLoadStartedRef.current = null;
      if (!queryProcessed) setQueryProcessed(true);
      return;
    }
    if (queryProcessed) return;

    const queryId = queryData.saved_query_id ?? queryData.query_name ?? "query";
    if (savedQueryLoadStartedRef.current === queryId) return;
    savedQueryLoadStartedRef.current = queryId;

    // Open saved query in a new tab: addSheetWithData prepends one tab and never removes existing tabs.
    const sheetData = {
      user_db_id: queryData?.user_db_id ?? null,
      // Preserve the saved query owner so we can make shared tabs view-only.
      owner_id: queryData?.user_id ?? null,
      columns: queryData.scenerio_json?.columns || [],
      rows: queryData.scenerio_json?.rows || [],
      data: queryData.scenerio_json?.data || [],
      filters: queryData.scenerio_json?.filters || [],
      aggregations: queryData.scenerio_json?.aggregations || [],
      dbtype:
        queryData.scenerio_json?.dbtype ||
        StorageHelper.get("selected_user_db_type"),
      dataFilters: queryData.scenerio_json?.dataFilters || {},
      // If the saved scenario contains a previously rendered table,
      // use it so that updating a saved query does not blank the table.
      queryResultTableData:
        queryData.scenerio_json?.queryResultTableData || null,
      // Prefer the stored generatedSqlQuery from scenario JSON if present,
      // otherwise fall back to the main query string.
      generatedSqlQuery:
        queryData.scenerio_json?.generatedSqlQuery || queryData.query || "",
      calculatedColumns: queryData.scenerio_json?.calculatedColumns || [],
    };

    const loadSavedQuery = async () => {
      try {
        await addSheetWithData(
          sheetData,
          queryData.query_name,
          queryData.saved_query_id
        );
        setQueryName(queryData.query_name);
      } catch (error) {}
    };

    loadSavedQuery();
    setQueryProcessed(true);
    window.history.replaceState({}, document.title);
  }, [queryData, queryProcessed]);

  useEffect(() => {
    if (saveQuerySuccess && !queryData) {
      const newQuery = addSavedQueryData?.addSavedQuery;
      if (newQuery) {
        setQueryData(newQuery);
      }
    }
  }, [saveQuerySuccess]);

  // Proper React solution to regenerate SQL after state updates (replaces setTimeout)
  // This useEffect watches the shouldRegenerateSQL flag and triggers SQL regeneration
  // when items are removed from columns/rows/data zones
  useEffect(() => {
    if (shouldRegenerateSQL) {
      // Reset flag first to prevent infinite loops
      setShouldRegenerateSQL(false);

      // Check if all zones are empty - if so, clear the table instead of regenerating SQL
      if (columns.length === 0 && rows.length === 0 && data.length === 0) {
        setGeneratedSqlQuery("");
        setQueryResultTableData(null);
      } else {
        console.log("[useEffect] Executing handleGenerateSQL");
        handleGenerateSQL();
      }
    }
    // Note: dependencies include all inputs that affect the query so the rerun
    // uses the latest state when the flag is set.
  }, [
    shouldRegenerateSQL,
    columns,
    rows,
    data,
    filters,
    aggregations,
    dataFilters,
    computedColumns,
  ]);

  const formatSqlQuery = (query) => {
    if (!query) return "";
    // Fix: Replace triple or more double quotes with a single pair
    let cleaned = query.replace(/"{3,}/g, '"');
    // Also replace any double double quotes with a single pair
    cleaned = cleaned.replace(/"{2,}/g, '"');
    // Basic SQL formatting
    let formatted = cleaned
      // Add line breaks after common SQL keywords
      .replace(
        /\b(SELECT|FROM|WHERE|GROUP BY|ORDER BY|HAVING|JOIN|LEFT JOIN|RIGHT JOIN|INNER JOIN|OUTER JOIN|UNION|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP)\b/gi,
        "\n$1"
      )
      // Add line breaks before common SQL keywords
      .replace(
        /\b(AND|OR|IN|NOT IN|LIKE|NOT LIKE|IS NULL|IS NOT NULL|BETWEEN|EXISTS|NOT EXISTS)\b/gi,
        "\n$1"
      )
      // Add line breaks after commas
      .replace(/,/g, ",\n")
      // Add line breaks after opening parentheses
      .replace(/\(/g, "(\n")
      // Add line breaks before closing parentheses
      .replace(/\)/g, "\n)")
      // Clean up multiple line breaks
      .replace(/\n\s*\n/g, "\n")
      // Add proper indentation
      .split("\n")
      .map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return "";
        // Determine indentation level
        let indent = 0;
        if (trimmed.startsWith(")")) indent = 0;
        else if (
          trimmed.match(
            /^(AND|OR|IN|NOT IN|LIKE|NOT LIKE|IS NULL|IS NOT NULL|BETWEEN|EXISTS|NOT EXISTS)/i
          )
        )
          indent = 2;
        else if (
          trimmed.match(
            /^(FROM|WHERE|GROUP BY|ORDER BY|HAVING|JOIN|LEFT JOIN|RIGHT JOIN|INNER JOIN|OUTER JOIN|UNION)/i
          )
        )
          indent = 0;
        else if (trimmed.startsWith(",")) indent = 2;
        else if (trimmed.startsWith("(")) indent = 0;
        else indent = 2;
        return "  ".repeat(indent) + trimmed;
      })
      .filter((line) => line !== "")
      .join("\n");
    return formatted;
  };

  const formattedSqlQuery = formatSqlQuery(generatedSqlQuery);
  const activeSheetSavedSql = React.useMemo(() => {
    if (
      !Array.isArray(sheets) ||
      activeSheetIndex < 0 ||
      activeSheetIndex >= sheets.length
    ) {
      return "";
    }
    const currentSheet = sheets[activeSheetIndex];
    if (!currentSheet?.saved_query_id) return "";

    return String(
      currentSheet?.last_saved_query ??
      queryData?.query ??
      currentSheet?.data?.generatedSqlQuery ??
      ""
    ).trim();
  }, [sheets, activeSheetIndex, queryData]);

  const hasSqlChanges = React.useMemo(
    () => String(generatedSqlQuery || "").trim() !== activeSheetSavedSql,
    [generatedSqlQuery, activeSheetSavedSql]
  );

  const isModalOpen = Boolean(sqlGenError);
  const handleModalClose = () => {
    setSqlGenError("");
    // Clear failed column state when error modal is closed
    setFailedCalculatedColumn(null);
  };

  const handleTryAgain = () => {
    // Close the error modal
    setSqlGenError("");

    // Open the edit modal with the failed column data
    if (failedCalculatedColumn) {
      setEditColumnName(failedCalculatedColumn.columnName);
      setEditFormula(failedCalculatedColumn.formula);
      setAddColumnModalOpen(true);
    }
  };

  const handleExpand = (id) => {
    setExpandedMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const removeItem = (zone, itemToRemove) => {

    const filterFn = (item) =>
      !(item.name === itemToRemove.name && item.dbname === itemToRemove.dbname);
    if (zone === "columns") {
      setColumns((prev) => {
        const filtered = prev.filter(filterFn);
        return filtered;
      });
    }
    if (zone === "rows") {
      setRows((prev) => {
        const filtered = prev.filter(filterFn);
        return filtered;
      });
    }
    if (zone === "data") {
      setData((prev) => {
        const filtered = prev.filter(filterFn);
        return filtered;
      });
      // Remove any aggregations for this column
      setAggregations((prev) => {
        const filtered = prev.filter((agg) => agg.columnName !== itemToRemove.name);
        return filtered;
      });
    }
    if (zone === "filters") {
      setFilters((prev) => {
        const filtered = prev.filter(filterFn);
        return filtered;
      });
      // Remove any filter conditions for this column
      setDataFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[itemToRemove.name];
        return newFilters;
      });
      // Remove any pending filter state for this column
      setPendingValuesByCondition((prev) => {
        const newPending = { ...prev };
        Object.keys(newPending).forEach((cond) => {
          if (
            cond.startsWith(itemToRemove.name + "||") ||
            cond === itemToRemove.name
          ) {
            delete newPending[cond];
          }
        });
        return newPending;
      });
      // If the currently open filter is this one, reset pendingFilter
      if (filterMenuCol === itemToRemove.name) {
        setPendingFilter({ condition: "eq", value: undefined });
      }
    }
    // SQL is only regenerated when user clicks "Run Query" button, not on remove
  };

  // Helper to check if a column is numeric
  const isNumericColumn = (item) => {
    // Try to get the type from columnTypes or item.textDatatype
    const type = columnTypes?.[item.name] || item.textDatatype || item.type ||"";
    // Accept common numeric types
    return [
      "int",
      "integer",
      "float",
      "double",
      "decimal",
      "number",
      "numeric",
      "bigint",
      "smallint",
      "tinyint",
      "real",
    ].some((t) => type?.toLowerCase().includes(t));
  };

  // Modified handleDrop to restrict Data zone to numeric columns only
  const handleDragOver = (zone) => (e) => {
    e.preventDefault();
    setDragOver(zone);
  };

  const handleDragLeave = () => setDragOver("");

  // Scope pending filter values by column+condition to avoid cross-tab leakage
  // (e.g. switching tabs shouldn't reuse "nin" values from a different sheet/column).
  const pendingKey = React.useCallback(
    (colName, condition) => `${String(colName || "").trim()}||${String(condition || "").trim()}`,
    []
  );

  // When opening the filter menu, always reset pendingFilter and pendingValuesByCondition for that column
  const handleFilterMenuOpen = (colName, event) => {
    setFilterMenuAnchor(event.currentTarget);
    setFilterMenuCol(colName);
    setFilterSearch("");
    setFilterLoading(true);
    setFilterError(null);
    // If there is an existing filter for this column, set pendingFilter to match it
    const existing = Array.isArray(dataFilters[colName])
      ? dataFilters[colName]
      : [];
    if (existing.length > 0) {
      // Use the last applied filter for this column
      const last = existing[existing.length - 1];
      setPendingFilter({
        condition: last.condition,
        value: last.value,
      });
      // Initialize pendingValuesByCondition with all existing filter values
      const newPendingValues = {};
      existing.forEach((filter) => {
        newPendingValues[pendingKey(colName, filter.condition)] = filter.value;
      });
      setPendingValuesByCondition((prev) => ({
        ...prev,
        ...newPendingValues,
      }));
    } else {
      // No filter applied, reset to default
      setPendingFilter({ condition: "eq", value: undefined });
      setPendingValuesByCondition((prev) => {
        const newPending = { ...prev };
        Object.keys(newPending).forEach((cond) => {
          if (cond.startsWith(colName + "||") || cond === colName) {
            delete newPending[cond];
          }
        });
        return newPending;
      });
    }
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
    setFilterMenuCol(null);
    setFilterSearch("");
    setFilterOptions([]);
    setFilterLoading(false);
    setFilterError(null);
  };

  const FILTER_PAGE_SIZE = 50;

  // Fetch options on search change
  React.useEffect(() => {
    if (!filterMenuCol || filterSearch === undefined) return;
    let active = true;

    const coerceToArray = (val) => {
      if (val === undefined || val === null || val === "") return [];
      return Array.isArray(val) ? val : [val];
    };

    const buildSelectedValuesForCurrentColumn = () => {
      const selected = new Set();

      // Include currently pending selection(s) (most up-to-date while popper is open)
      coerceToArray(pendingFilter?.value).forEach((v) => selected.add(String(v)));

      // Also include any already-applied filter values for this column
      const existing = Array.isArray(dataFilters?.[filterMenuCol])
        ? dataFilters[filterMenuCol]
        : [];
      existing.forEach((f) => {
        coerceToArray(f?.value).forEach((v) => selected.add(String(v)));
      });

      return Array.from(selected).filter((v) => v !== "");
    };

    const mergeOptionsWithSelectedValues = (apiValues, selectedValues) => {
      const seen = new Set();
      const merged = [];

      // Keep API ordering first
      (apiValues || []).forEach((v) => {
        const sv = String(v);
        if (sv && !seen.has(sv)) {
          seen.add(sv);
          merged.push({ label: sv, value: sv });
        }
      });

      // Then append selected values that aren't in the first-page API payload
      (selectedValues || []).forEach((sv) => {
        const v = String(sv);
        if (v && !seen.has(v)) {
          seen.add(v);
          merged.push({ label: v, value: v });
        }
      });

      return merged;
    };

    const fetchOptions = async () => {
      setFilterLoading(true);
      setFilterError(null);
      try {
        const userDbId = String(StorageHelper.get("selected_user_db_id") || "");
        const tableName =
          (
            data.find((d) => d.name === filterMenuCol) ||
            columns.find((d) => d.name === filterMenuCol) ||
            rows.find((d) => d.name === filterMenuCol) ||
            filters.find((d) => d.name === filterMenuCol)
          )?.dbname || "";
        const db_type = getCurrentDbType();
        const columnName = filterMenuCol;
        const result = await getRecordsByColumnNameApi(
          String(userId),
          userDbId,
          db_type,
          tableName,
          columnName,
          filterSearch,
          0,
          FILTER_PAGE_SIZE
        );
        if (active) {
          if (result.success) {
            const apiValues = (result.data || []).map((item) => item?.value);
            const selectedValues = buildSelectedValuesForCurrentColumn();
            setFilterOptions(mergeOptionsWithSelectedValues(apiValues, selectedValues));
            setFilterHasMore((result.data || []).length >= FILTER_PAGE_SIZE);
          } else {
            setFilterOptions([]);
            setFilterError(result.error);
          }
        }
      } catch (err) {
        if (active) {
          setFilterOptions([]);
          setFilterError(err.message || "Failed to load filter options");
        }
      } finally {
        if (active) setFilterLoading(false);
      }
    };
    fetchOptions();
    return () => {
      active = false;
    };
  }, [filterSearch, filterMenuCol]);

  const handleFilterLoadMore = async (colName) => {
    if (!colName || filterLoadingMore) return;
    try {
      setFilterLoadingMore(true);
      setFilterError(null);

      const userDbId = String(StorageHelper.get("selected_user_db_id") || "");
      const tableName =
        (
          data.find((d) => d.name === colName) ||
          columns.find((d) => d.name === colName) ||
          rows.find((d) => d.name === colName) ||
          filters.find((d) => d.name === colName)
        )?.dbname || "";
      const db_type = getCurrentDbType();
      const offset = filterOptions.length;

      const result = await getRecordsByColumnNameApi(
        String(userId),
        userDbId,
        db_type,
        tableName,
        colName,
        filterSearch,
        offset,
        FILTER_PAGE_SIZE
      );

      if (result.success) {
        const apiValues = (result.data || []).map((item) =>
          item?.value != null ? String(item.value) : ""
        );

        setFilterOptions((prev) => {
          const existing = new Set(prev.map((opt) => opt.value));
          const toAdd = apiValues
            .filter((v) => v && !existing.has(v))
            .map((v) => ({ label: v, value: v }));
          return [...prev, ...toAdd];
        });

        setFilterHasMore((result.data || []).length >= FILTER_PAGE_SIZE);
      } else {
        setFilterHasMore(false);
      }
    } catch (err) {
      setFilterHasMore(false);
      setFilterError(err.message || "Failed to load more filter options");
    } finally {
      setFilterLoadingMore(false);
    }
  };

  const handleFilterConditionChange = (colName, condition) => {
    // Check if there's an existing filter for this condition
    const existingFilters = Array.isArray(dataFilters[colName])
      ? dataFilters[colName]
      : [];
    const existingFilter = existingFilters.find(f => f.condition === condition);

    setPendingFilter((prev) => ({
      condition,
      value:
        existingFilter?.value ||
        pendingValuesByCondition[pendingKey(colName, condition)] ||
        undefined,
    }));
    setFilterSearch("");
  };

  // Toggle OFF a condition: remove its selected values for that condition only.
  // This allows multiple conditions to coexist; switching conditions won't clear anything.
  const handleFilterConditionUnchecked = (colName, condition) => {
    // Remove from applied filters
    setDataFilters((prev) => {
      const prevArr = Array.isArray(prev[colName]) ? prev[colName] : [];
      const nextArr = prevArr.filter((f) => f?.condition !== condition);
      const next = { ...prev };
      if (nextArr.length > 0) next[colName] = nextArr;
      else delete next[colName];
      return next;
    });

    // Clear any remembered pending values for this condition
    setPendingValuesByCondition((prev) => {
      const next = { ...prev };
      delete next[pendingKey(colName, condition)];
      return next;
    });

    // If user is currently editing this condition, switch editor to a safe default
    setPendingFilter((prev) => {
      if (prev?.condition !== condition) return prev;

      const remaining = Array.isArray(dataFilters?.[colName])
        ? dataFilters[colName].filter((f) => f?.condition !== condition)
        : [];

      const fallbackCond = remaining?.[remaining.length - 1]?.condition || "eq";
      const fallbackVal = remaining?.[remaining.length - 1]?.value;
      return { condition: fallbackCond, value: fallbackVal };
    });
  };

  // Robust filter value change handler (now updates dataFilters immediately)
  const handleFilterValueChange = (colName, value) => {
    setPendingFilter((prev) => {
      let newObj = { ...prev };
      // Multi-select conditions
      const multiSelectConds = ["in", "nin", "eq", "neq"];
      // Single-value conditions
      const singleValueConds = ["gt", "lt", "gte", "lte", "like", "between"];
      if (multiSelectConds.includes(prev.condition)) {
        const arr = Array.isArray(prev.value) ? prev.value : [];
        const exists = arr.includes(value);
        if (exists) {
          // If unchecked, remove from array
          const newArr = arr.filter((v) => v !== value);
          newObj.value = newArr;
        } else {
          newObj.value = [...arr, value];
        }
      } else if (singleValueConds.includes(prev.condition)) {
        // Single-value: clicking again unsets
        if (prev.value === value) {
          newObj.value = undefined;
        } else {
          newObj.value = value;
        }
      } else {
        // Default single-value
        if (prev.value === value) {
          newObj.value = undefined;
        } else {
          newObj.value = value;
        }
      }
      // Save to pendingValuesByCondition
      setPendingValuesByCondition((pv) => ({
        ...pv,
        [pendingKey(colName, prev.condition)]: newObj.value,
      }));
      // Immediately update dataFilters
      setDataFilters((prev) => {
        const prevArr = Array.isArray(prev[colName]) ? prev[colName] : [];
        if (
          newObj.condition &&
          (["null", "notnull"].includes(newObj.condition) ||
            (Array.isArray(newObj.value)
              ? newObj.value.length > 0
              : newObj.value !== undefined &&
              newObj.value !== null &&
              newObj.value !== ""))
        ) {
          // Remove any existing filter for this condition
          const filteredArr = prevArr.filter(
            (f) => f.condition !== newObj.condition
          );
          return { ...prev, [colName]: [...filteredArr, { ...newObj }] };
        } else {
          // Remove the filter if value is empty/undefined
          const filteredArr = prevArr.filter(
            (f) => f.condition !== newObj.condition
          );
          const newFilters = { ...prev };
          if (filteredArr.length > 0) {
            newFilters[colName] = filteredArr;
          } else {
            delete newFilters[colName];
          }
          return newFilters;
        }
      });

      // Ensure selected values remain visible in the list even when the API only returns a first page
      setFilterOptions((prevOptions) => {
        const seen = new Set((prevOptions || []).map((o) => String(o?.value)));
        const next = [...(prevOptions || [])];

        const pushIfMissing = (v) => {
          const sv = String(v);
          if (!sv || seen.has(sv)) return;
          seen.add(sv);
          next.push({ label: sv, value: sv });
        };

        if (Array.isArray(newObj.value)) {
          newObj.value.forEach(pushIfMissing);
        } else {
          pushIfMissing(newObj.value);
        }

        return next;
      });

      return newObj;
    });
  };

  // Robust filter apply handler
  const handleFilterApply = (colName) => {
    setDataFilters((prev) => {
      const prevArr = Array.isArray(prev[colName]) ? prev[colName] : [];
      // If the filter is valid, add/update it
      if (
        pendingFilter.condition &&
        (["null", "notnull"].includes(pendingFilter.condition) ||
          (Array.isArray(pendingFilter.value)
            ? pendingFilter.value.length > 0
            : pendingFilter.value !== undefined &&
            pendingFilter.value !== null &&
            pendingFilter.value !== ""))
      ) {
        // Remove any existing filter for this condition
        const filteredArr = prevArr.filter(
          (f) => f.condition !== pendingFilter.condition
        );
        return { ...prev, [colName]: [...filteredArr, { ...pendingFilter }] };
      } else {
        // Remove the filter if value is empty/undefined
        const filteredArr = prevArr.filter(
          (f) => f.condition !== pendingFilter.condition
        );
        const newFilters = { ...prev };
        if (filteredArr.length > 0) {
          newFilters[colName] = filteredArr;
        } else {
          delete newFilters[colName];
        }
        return newFilters;
      }
    });
    setPendingValuesByCondition((prev) => {
      const newPending = { ...prev };
      delete newPending[pendingKey(colName, pendingFilter.condition)];
      return newPending;
    });
    handleFilterMenuClose();
  };

  const handleRemoveFilter = (colName, idx) => {
    setDataFilters((prev) => {
      const arr = Array.isArray(prev[colName]) ? prev[colName] : [];
      const newArr = arr.filter((_, i) => i !== idx);
      // If no filters left for this column, remove the key entirely
      const newFilters = { ...prev };
      if (newArr.length > 0) {
        newFilters[colName] = newArr;
      } else {
        delete newFilters[colName];
      }
      return newFilters;
    });
  };

  const handleFilterClear = (colName) => {
    setPendingFilter({ condition: "eq", value: undefined });
    setDataFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[colName];
      return newFilters;
    });
    handleFilterMenuClose();
  };

  const renderZone = (label, items, zone) => {
    // For Data zone, include computed columns
    const allItems = zone === "data"
      ? [...items, ...(computedColumns || []).map(cc => ({
        name: cc.columnName,
        dbname: commonTableName || "calculated",
        isComputed: true,
        formula: cc.formula
      }))]
      : items;

    const handleDragOverItem = (e, index) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      const mouseY = e.clientY;
      const insertIndex = mouseY < midpoint ? index : index + 1;
      setDropIndex({ zone, index: insertIndex });
    };

    const handleDragLeaveItem = () => {
      setDropIndex({ zone: null, index: null });
    };

    return (
      <ZoneContainer>
        <DropLabel>{label}:</DropLabel>
        <DropZone
          dragover={dragOver === zone}
          onDrop={(e) => {
            const index = dropIndex.zone === zone ? dropIndex.index : null;
            handleDrop(zone, index)(e);
            setDropIndex({ zone: null, index: null });
          }}
          onDragOver={handleDragOver(zone)}
          onDragLeave={() => {
            handleDragLeave();
            setDropIndex({ zone: null, index: null });
          }}
        >
          {allItems.length === 0 ? (
            <DropText>Drop {zone} here</DropText>
          ) : (
            <Box sx={{ width: "100%" }}>
              {allItems.map((item, idx) => {
                const normalizedName = (item.name || "").trim();
                const selectedAgg =
                  aggregations.find(
                    (agg) => (agg.columnName || "").trim() === normalizedName
                  )?.fn || "";
                const isComputedColumn = item.isComputed === true;
                const showIndicatorAbove = dropIndex.zone === zone && dropIndex.index === idx;

                return (
                  <React.Fragment key={`${item.dbname}-${item.name}-${isComputedColumn ? 'computed' : 'regular'}-${idx}`}>
                    <DropIndicator isVisible={showIndicatorAbove} />
                    <DropItemCard
                      draggable={!isComputedColumn}
                      onDragStart={(e) => {
                        if (!isComputedColumn) {
                          e.dataTransfer.setData(
                            "text/plain",
                            JSON.stringify({ ...item, __sourceZone: zone,__sourceIndex: idx })
                          );
                        }
                      }}
                      onDragOver={(e) => handleDragOverItem(e, idx)}
                      onDragLeave={handleDragLeaveItem}
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                        ...(isComputedColumn && {
                          backgroundColor: "rgba(44, 52, 123, 0.05)",
                          border: "1px dashed rgba(44, 52, 123, 0.3)",
                        }),
                      }}
                    >
                      <Typography
                        sx={{
                          color: isComputedColumn ? "rgb(44, 52, 123)" : "text.primary",
                          fontWeight: isComputedColumn ? 600 : 500,
                          fontSize: 10,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          flex: 1,
                          position: "relative",
                        }}
                        title={
                          isComputedColumn
                            ? `Calculated: ${item.formula}`
                            : (item.dbname ? `Table: ${item.dbname}` : item.name)
                        }
                      >
                        {isComputedColumn && "ƒ "}{item.name}
                      </Typography>
                      {zone === "data" && !isComputedColumn && (
                        <Dropdown
                          options={AGG_FUNCTIONS}
                          value={selectedAgg}
                          onChange={(val) => {
                            setAggregations((prev) => {
                              const filtered = prev.filter(
                                (agg) =>
                                  (agg.columnName || "").trim() !== normalizedName
                              );
                              return val
                                ? [
                                    ...filtered,
                                    { fn: val, columnName: normalizedName },
                                  ]
                                : filtered;
                            });
                          }}
                          placeholder="Add Aggregation"
                          style={{ minWidth: 120, marginRight: 8 }}
                        />
                      )}
                      {isComputedColumn && (
                        <IconButton
                          size="small"
                          aria-label="Edit"
                          onClick={() => {
                            setEditColumnName(item.name);
                            setEditFormula(item.formula);
                            setAddColumnModalOpen(true);
                          }}
                          sx={{
                            ml: 0.5,
                            color: "rgb(44, 52, 123)",
                            p: "1px",
                            "&:hover": {
                              color: "rgba(44, 52, 123, 0.8)",
                              backgroundColor: "rgba(44, 52, 123, 0.1)",
                            },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        aria-label="Remove"
                        onClick={() => {
                          if (isComputedColumn) {
                            removeComputedColumn(item.name);
                            // Re-run SQL once so the table reflects the removed calculated column.
                            setShouldRegenerateSQL(true);
                          } else {
                            removeItem(zone, item);
                            // For non-calculated items, do NOT auto-run SQL.
                            // User controls refresh via "Run Query".
                          }
                        }}
                        sx={{
                          ml: 0.5,
                          color: "text.secondary",
                          p: "1px",
                          "&:hover": {
                            color: "text.primary",
                            backgroundColor: "action.selected",
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </DropItemCard>
                    {idx === allItems.length - 1 && (
                      <DropIndicator
                        isVisible={dropIndex.zone === zone && dropIndex.index === allItems.length}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          )}
        </DropZone>
      </ZoneContainer>
    );
  };
  const getAvailableFilterConditions = (item) => {
    // Check if column is numeric
    const isNumeric = isNumericColumn(item);
    if (isNumeric) {
      // For numeric columns, show all conditions
      return FILTER_CONDITIONS;
    } else {
      // For text columns, exclude comparison operators (gt, lt)
      return FILTER_CONDITIONS.filter(
        (cond) => cond.value !== "gt" && cond.value !== "lt"
      );
    }
  };

  // Add renderZone for Filter dropzone
  const renderFilterZone = (label, items, zone) => {
    const handleDragOverItem = (e, index) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      const mouseY = e.clientY;

      // Determine if we should insert before or after this item
      const insertIndex = mouseY < midpoint ? index : index + 1;
      setDropIndex({ zone, index: insertIndex });
    };

    const handleDragLeaveItem = () => {
      setDropIndex({ zone: null, index: null });
    };

    return (
      <ZoneContainer>
        <DropLabel>{label}:</DropLabel>
        <DropZone
          dragover={dragOver === zone}
          onDrop={(e) => {
            const index = dropIndex.zone === zone ? dropIndex.index : null;
            handleDrop(zone, index)(e);
            setDropIndex({ zone: null, index: null });
          }}
          onDragOver={handleDragOver(zone)}
          onDragLeave={() => {
            handleDragLeave();
            setDropIndex({ zone: null, index: null });
          }}
        >
          {items.length === 0 ? (
            <DropText>Drop {zone} here</DropText>
          ) : (
            <Box sx={{ width: "100%" }}>
              {items.map((item, idx) => {
                const showIndicatorAbove = dropIndex.zone === zone && dropIndex.index === idx;
                const activeFilters = Array.isArray(dataFilters[item.name])
                  ? dataFilters[item.name]
                  : [];
                const activeConditionValues = new Set(
                  activeFilters.map(f => f.condition)
                );

                return (
                  <React.Fragment key={`${item.dbname}-${item.name}-${idx}`}>
                    <DropIndicator isVisible={showIndicatorAbove} />
                    <DropItemCard
                      draggable={true}
                      onDragStart={(e) => {
                        e.dataTransfer.setData(
                          "text/plain",
                          JSON.stringify({
                            ...item,
                            __sourceZone: zone,
                            __sourceIndex: idx
                          })
                        );
                      }}
                      onDragOver={(e) => handleDragOverItem(e, idx)}
                      onDragLeave={handleDragLeaveItem}
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "text.primary",
                          fontWeight: 500,
                          fontSize: 10,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          flex: 1,
                          position: "relative",
                        }}
                        title={item.dbname ? `Table: ${item.dbname}` : item.name}
                      >
                        {item.name}
                      </Typography>
                      {/* Filter icon and popper for Filter zone only */}
                      <>
                        <IconButton
                          size="small"
                          aria-label="Filter"
                          onClick={(e) => {
                            handleFilterMenuOpen(item.name, e);
                            filterIconRefs.current[item.name] = e.currentTarget;
                          }}
                          sx={{ ml: 0.5, color: "rgb(44, 52, 123)", p: "2px" }}
                          ref={(el) => (filterIconRefs.current[item.name] = el)}
                        >
                          <FilterListIcon fontSize="small" />
                        </IconButton>
                        <Popper
                          open={filterMenuAnchor && filterMenuCol === item.name}
                          anchorEl={filterIconRefs.current[item.name]}
                          placement="bottom-start"
                          style={{ zIndex: 2000 }}
                          disablePortal={false}
                          modifiers={[
                            { name: "offset", options: { offset: [0, 8] } },
                            {
                              name: "preventOverflow",
                              options: { boundary: "viewport", padding: 8 },
                            },
                            { name: "flip", enabled: true },
                          ]}
                        >
                          <ClickAwayListener onClickAway={handleFilterMenuClose}>
                            {/* ...existing filter popper code... */}
                            {/* Copy the filter popper JSX from Data zone here */}
                            <Paper
                              elevation={8}
                              sx={{
                                borderRadius: 3,
                                minWidth: 340,
                                maxWidth: 400,
                                maxHeight: "min(520px, calc(100vh - 100px))",
                                p: 0,
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                boxShadow: "0 6px 32px 0 rgba(60,72,100,0.18)",
                              }}
                            >
                              {/* Header */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  px: 2,
                                  py: 1.2,
                                  borderBottom: "1px solid #e5e7eb",
                                  bgcolor: "#f6f8fa",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 15,
                                    color: "rgb(44, 52, 123)",
                                  }}
                                >
                                  Filter
                                </Typography>
                                <Button
                                  size="small"
                                  color="inherit"
                                  sx={{
                                    fontSize: 12,
                                    textTransform: "none",
                                    fontWeight: 500,
                                    px: 1,
                                  }}
                                  onClick={() => handleFilterClear(item.name)}
                                >
                                  Clear
                                </Button>
                              </Box>
                              {/* Two-column layout */}
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "stretch",
                                  flex: 1,
                                  minHeight: 0,
                                  overflow: "hidden",
                                }}
                              >
                                {/* Left: Condition list */}
                                <Box
                                  sx={{
                                    width: 140,
                                    flexShrink: 0,
                                    py: 1,
                                    px: 1,
                                    borderRight: "1px solid #e5e7eb",
                                    bgcolor: "#f8fafc",
                                  }}
                                >
                                  {getAvailableFilterConditions(item).map((cond) => {
                                    const isConditionActive = activeConditionValues.has(cond.value);
                                    const isPendingSelected = pendingFilter.condition === cond.value;
                                    const isSelected = isConditionActive || isPendingSelected;

                                    return (
                                      <DropdownOption
                                        key={cond.value}
                                        isSelected={isSelected}
                                        onClick={() =>
                                          handleFilterConditionChange(
                                            item.name,
                                            cond.value
                                          )
                                        }
                                        sx={{
                                          fontWeight: isSelected ? 700 : 400,
                                          color: isSelected ? "rgb(44, 52, 123)" : "#232323",
                                          bgcolor: isSelected
                                            ? "rgba(63,73,167,0.07)"
                                            : "transparent",
                                          borderRadius: 2,
                                          mb: 0.5,
                                          px: 1,
                                          py: 0.7,
                                          "&:hover": {
                                            bgcolor: "rgba(63,73,167,0.10)",
                                          },
                                        }}
                                      >
                                        <SelectionIndicator
                                          isSelected={isConditionActive}
                                          onClick={(e) => {
                                            // Only uncheck/remove when clicking the checkbox itself.
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const condition = cond.value;
                                            const colName = item.name;

                                            // Null / Not null should be applied via checkbox toggle
                                            if (["null", "notnull"].includes(condition)) {
                                              if (isConditionActive) {
                                                handleFilterConditionUnchecked(colName, condition);
                                              } else {
                                                // Apply this condition (no value needed)
                                                setDataFilters((prev) => {
                                                  const prevArr = Array.isArray(prev[colName])
                                                    ? prev[colName]
                                                    : [];
                                                  const nextArr = prevArr.filter(
                                                    (f) => f?.condition !== condition
                                                  );
                                                  return {
                                                    ...prev,
                                                    [colName]: [
                                                      ...nextArr,
                                                      { condition, value: undefined },
                                                    ],
                                                  };
                                                });
                                                setPendingFilter({ condition, value: undefined });
                                                setFilterSearch("");
                                              }
                                              return;
                                            }

                                            // For other conditions:
                                            // - If active: checkbox removes this condition
                                            // - If not active: checkbox opens that condition's value list (does not apply)
                                            if (isConditionActive) {
                                              handleFilterConditionUnchecked(colName, condition);
                                            } else {
                                              handleFilterConditionChange(colName, condition);
                                            }
                                          }}
                                          sx={{
                                            cursor: "pointer",
                                          }}
                                        >
                                          <CheckIcon
                                            sx={{
                                              fontSize: 10,
                                              opacity: isConditionActive ? 1 : 0,
                                            }}
                                          />
                                        </SelectionIndicator>
                                        <span className="item-label">{cond.label}</span>
                                      </DropdownOption>
                                    );
                                  })}
                                </Box>
                                {/* Right: Value selection */}
                                <Box
                                  sx={{
                                    flex: 1,
                                    py: 1.5,
                                    px: 2,
                                    minWidth: 120,
                                    minHeight: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "flex-start",
                                    overflow: "hidden",
                                  }}
                                >
                                  {pendingFilter.condition &&
                                    !["null", "notnull"].includes(
                                      pendingFilter.condition
                                    ) && (
                                      <>
                                        <DropdownSearchInput
                                          placeholder="Search..."
                                          value={filterSearch}
                                          onChange={(e) =>
                                            setFilterSearch(e.target.value)
                                          }
                                          autoFocus
                                        />
                                        {filterLoading && filterOptions.length === 0 ? (
                                          <Box
                                            sx={{
                                              fontSize: 12,
                                              px: 1,
                                              opacity: 0.7,
                                              py: 1,
                                            }}
                                          >
                                            Loading...
                                          </Box>
                                        ) : filterError ? (
                                          <Box
                                            sx={{
                                              fontSize: 12,
                                              px: 1,
                                              opacity: 0.7,
                                              py: 1,
                                              color: "error.main",
                                            }}
                                          >
                                            {filterError}
                                          </Box>
                                        ) : filterOptions.length === 0 ? (
                                          <Box
                                            sx={{
                                              fontSize: 12,
                                              px: 1,
                                              opacity: 0.6,
                                              py: 1,
                                            }}
                                          >
                                            No results found
                                          </Box>
                                        ) : (
                                          <Box
                                            sx={{
                                              flex: 1,
                                              minHeight: 0,
                                              overflowY: "auto",
                                              mt: 0.5,
                                            }}
                                          >
                                          {filterOptions.map((option) => (
                                            (() => {
                                              const isMulti =
                                                ["in", "nin", "eq", "neq"].includes(
                                                  pendingFilter.condition
                                                );
                                              const isSelected = isMulti
                                                ? Array.isArray(pendingFilter.value) &&
                                                pendingFilter.value.includes(option.value)
                                                : pendingFilter.value === option.value;

                                              const isSingleSelectCond = [
                                                "gt",
                                                "lt",
                                                "gte",
                                                "lte",
                                                "like",
                                                "between",
                                              ].includes(pendingFilter.condition);

                                              return (
                                                <DropdownOption
                                                  key={option.value}
                                                  isSelected={(() => {
                                                    if (
                                                      ["in", "nin", "eq", "neq"].includes(
                                                        pendingFilter.condition
                                                      )
                                                    ) {
                                                      return (
                                                        Array.isArray(
                                                          pendingFilter.value
                                                        ) &&
                                                        pendingFilter.value.includes(
                                                          option.value
                                                        )
                                                      );
                                                    }
                                                    return (
                                                      pendingFilter.value === option.value
                                                    );
                                                  })()}
                                                  onClick={() =>
                                                    handleFilterValueChange(
                                                      item.name,
                                                      option.value
                                                    )
                                                  }
                                                >
                                                  {isSingleSelectCond ? (
                                                    <RadioIndicator isSelected={isSelected} />
                                                  ) : (
                                                    <SelectionIndicator isSelected={isSelected}>
                                                      <CheckIcon
                                                        sx={{
                                                          fontSize: 10,
                                                          opacity: 1,
                                                        }}
                                                      />
                                                    </SelectionIndicator>
                                                  )}
                                                  <span className="item-label">
                                                    {option.label}
                                                  </span>
                                                </DropdownOption>
                                              );
                                            })()
                                          ))}
                                          {filterHasMore && !filterLoadingMore && (
                                            <Box
                                              sx={{
                                                mt: 0.5,
                                                fontSize: 12,
                                                px: 1,
                                                py: 0.75,
                                                textAlign: "center",
                                                color: "rgb(44, 52, 123)",
                                                cursor: "pointer",
                                                borderTop: "1px solid #e5e7eb",
                                                "&:hover": {
                                                  textDecoration: "underline",
                                                  backgroundColor: "rgba(63,73,167,0.04)",
                                                },
                                              }}
                                              onClick={() => handleFilterLoadMore(item.name)}
                                            >
                                              Load more
                                            </Box>
                                          )}
                                          {filterLoadingMore && (
                                            <Box
                                              sx={{
                                                mt: 0.5,
                                                fontSize: 12,
                                                px: 1,
                                                py: 0.75,
                                                textAlign: "center",
                                                color: "text.secondary",
                                              }}
                                            >
                                              Loading...
                                            </Box>
                                          )}
                                          </Box>
                                        )}
                                      </>
                                    )}
                                  {pendingFilter.condition &&
                                    ["null", "notnull"].includes(
                                      pendingFilter.condition
                                    ) && (
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          color: "#888",
                                          mt: 2,
                                        }}
                                      >
                                        No value needed for this condition.
                                      </Typography>
                                    )}
                                </Box>
                              </Box>
                              <Divider sx={{ my: 0 }} />
                              {/* Footer: Apply button */}
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "center",
                                  px: 2,
                                  py: 1,
                                  bgcolor: "#f6f8fa",
                                }}
                              >
                                {/* Remove the Apply button in the filter popper footer */}
                              </Box>
                            </Paper>
                          </ClickAwayListener>
                        </Popper>
                      </>
                      <IconButton
                        size="small"
                        aria-label="Remove"
                        onClick={() => removeItem(zone, item)}
                        sx={{
                          ml: 0.5,
                          color: "text.secondary",
                          p: "1px",
                          "&:hover": {
                            color: "text.primary",
                            backgroundColor: "action.selected",
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </DropItemCard>
                    {idx === items.length - 1 && (
                      <DropIndicator
                        isVisible={dropIndex.zone === zone && dropIndex.index === items.length}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          )}
        </DropZone>
      </ZoneContainer>
    );
  };

  const handleCloseUploadDialog = () => setUploadDialogOpen(false);

  const handleTypeChange = (header, newType) => {
    setColumnTypes((prev) => ({ ...prev, [header]: newType }));
  };

  const handleLoadData = async () => {
    if (!uploadedFiles.length || !uploadedFiles[0].rawFile) {
      alert("Please upload a new file to enable loading.");
      return;
    }
    const file = uploadedFiles[0];
    // Prepare schema JSON and convert to base64
    const columnsJson = uploadedHeaders.map((header) => ({
      header,
      type: columnTypes[header] || "VARCHAR",
    }));
    const schemaPayload = { columns: columnsJson };
    const schemaJson = JSON.stringify(schemaPayload);
    const schemaJsonBase64 = btoa(unescape(encodeURIComponent(schemaJson)));
    const schemaFilename = file.name.replace(/\.[^.]+$/, "") + "_schema.json";

    // Read file as base64
    const reader = new FileReader();
    reader.onload = async (e) => {
      const fileBase64 = e.target.result.split(",")[1];
      const filename = file.name;
      // Call the API
      const result = await uploadFileToMinio(
        fileBase64,
        filename,
        schemaJsonBase64,
        schemaFilename,
        userId
      );
      if (result.success) {
        alert("Upload successful!");
      } else {
        alert("Upload failed: " + result.error);
      }
    };
    reader.readAsDataURL(file.rawFile);
  };
  // At the top of the return, add a loading overlay or bar if any loading is true
  const anyLoading =
    tableLoading ||
    sqlLoading ||
    uploadLoading ||
    runQueryLoading ||
    getRecordsLoading ||
    filterLoading;
  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 64px)", // Subtracting header height
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "visible",
        position: "relative",
      }}
    >
      {/* Success Alert for edit mode save */}
      {editSaveSuccessAlert && (
        <Alert
          severity="success"
          sx={{
            position: "fixed",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3000,
            minWidth: 320,
            boxShadow: 3,
          }}
        >
          Query saved successfully!
        </Alert>
      )}
      {anyLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 2000,
          }}
        >
          <LinearProgress color="primary" />
        </Box>
      )}
      {/* Single unified card for all content - tabs, buttons, and table */}
      <Card
        elevation={1}
        sx={{
          m: 0,
          p: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Excel-like dynamic sheet tabs */}
        <Box
          sx={{
            width: "100%",
            px: 0,
            pt: 0,
            pb: 0,
            flexShrink: 0,
          }}
        >
          <ExcelSheetTabs
            sheets={sheets}
            activeSheetIndex={activeSheetIndex}
            onSheetChange={handleSwitchToSheet}
            onSheetAdd={(index) => {
              addSheet(index);
              // Clear queryData for new tabs so they create new queries
              setQueryData(null);
            }}
            onSheetRename={renameSheet}
            onSheetDelete={deleteSheet}
            onSheetDuplicate={(sheetIndex) => {
              duplicateSheet(sheetIndex);
              // Clear queryData for duplicated tabs so they create new queries
              setQueryData(null);
            }}
            onSheetReorder={reorderSheets}
            maxSheets={50}
            allowRename={true}
            allowDelete={true}
            allowDuplicate={true}
            allowReorder={true}
          />
        </Box>
        {/* Content area - left panel and right section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flex: 1,
            minHeight: 0,
            overflow: "visible",
            position: "relative",
          }}
        >
          {/* Expand/Collapse Toggle Button - Always visible */}
          <IconButton
            onClick={() => setIsLeftPanelExpanded(!isLeftPanelExpanded)}
            sx={{
              position: "absolute",
              left: isLeftPanelExpanded ? "calc(320px - 1px)" : "0px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1000,
              backgroundColor: "rgb(44, 52, 123)",
              color: "#fff",
              borderRadius: isLeftPanelExpanded ? "0 8px 8px 0" : "0 8px 8px 0",
              width: 24,
              height: 48,
              minWidth: 24,
              padding: 0,
              boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
              "&:hover": {
                backgroundColor: "rgba(44, 52, 123, 0.8)",
              },
              transition: "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              visibility: "visible",
              opacity: 1,
            }}
          >
            {isLeftPanelExpanded ? (
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>

          {/* Collapsible Left Panel */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              height: "100%",
              position: "relative",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >

            {/* Panel Content */}
            <Box
              sx={{
                width: isLeftPanelExpanded ? 320 : 0,
                overflow: "hidden",
                backgroundColor: "#ffffff",
                borderRight: "1px solid #e0e0e0",
                display: "flex",
                flexDirection: "column",
                transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {isLeftPanelExpanded && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                  }}
                >
                  {/* Scrollable Dropzones Section */}
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      overflowX: "hidden",
                      px: 1.5,
                      py: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      "&::-webkit-scrollbar": {
                        display: "none",
                        width: 0,
                        height: 0,
                      },
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}
                  >
                    {renderZone("Columns", columns, "columns")}
                    {renderZone("Rows", rows, "rows")}
                    {renderZone("Data", data, "data")}
                    {renderFilterZone("Filters", filters, "filters")}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Right Section - Buttons and Table (Vertical) */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            {/* Buttons Section - Above Table */}
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                px: 2,
                py: 1.25,
                borderBottom: "1px solid #e0e0e0",
                backgroundColor: "#ffffff",
                minHeight: 48,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (!hasQuerySelections) return;
                    handleGenerateSQL();
                  }}
                  startIcon={<BoltIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    background: "rgb(44, 52, 123)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 11,
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    minHeight: 32,
                    height: 32,
                    boxShadow: "0 1px 3px rgba(44, 52, 123, 0.2)",
                    textTransform: "none",
                    letterSpacing: 0.2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: "rgba(44, 52, 123, 0.85)",
                      boxShadow: "0 2px 6px rgba(44, 52, 123, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": {
                      background: "#e0e0e0",
                      color: "#999",
                      boxShadow: "none",
                      transform: "none",
                    },
                  }}
                  disabled={sqlLoading || !hasQuerySelections}
                >
                  {sqlLoading ? "Running Query..." : "Run Query"}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                   if (activeSheetIndex >= 0 && sheets[activeSheetIndex]) {
                      handleSaveQuery(sheets[activeSheetIndex], false);
                   }
                   }}
                  disabled={
                    !generatedSqlQuery ||
                    !generatedSqlQuery.trim() ||
                    (sheets?.[activeSheetIndex]?.saved_query_id &&
                      (sheets?.[activeSheetIndex]?.owner_id ??
                        queryData?.user_id) != null &&
                      userId != null &&
                      !Number.isNaN(Number(userId)) &&
                      Number(sheets?.[activeSheetIndex]?.owner_id ??
                        queryData?.user_id) !== Number(userId)) ||
                    (sheets?.[activeSheetIndex]?.saved_query_id && !hasSqlChanges)
                  }
                  startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    background: "rgb(44, 52, 123)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 11,
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    minHeight: 32,
                    height: 32,
                    boxShadow: "0 1px 3px rgba(44, 52, 123, 0.2)",
                    textTransform: "none",
                    letterSpacing: 0.2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: "rgba(44, 52, 123, 0.85)",
                      boxShadow: "0 2px 6px rgba(44, 52, 123, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": {
                      background: "#e0e0e0",
                      color: "#999",
                      boxShadow: "none",
                      transform: "none",
                    },
                  }}
                >
                  Save
                </Button>
                 {activeSheetIndex >= 0 &&
              sheets[activeSheetIndex]?.saved_query_id && (
                <Button
                  variant="contained"
                  onClick={() => {
                    if (sheets[activeSheetIndex]) {
                      handleSaveQuery(sheets[activeSheetIndex], true);
                    }
                  }}
                   disabled={
                    !generatedSqlQuery ||
                    !generatedSqlQuery.trim() ||
                    (sheets?.[activeSheetIndex]?.saved_query_id && !hasSqlChanges)
                  }
                  startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    background: "rgb(44, 52, 123)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 11,
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    minHeight: 32,
                    height: 32,
                    boxShadow: "0 1px 3px rgba(44, 52, 123, 0.2)",
                    textTransform: "none",
                    letterSpacing: 0.2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: "rgba(44, 52, 123, 0.85)",
                      boxShadow: "0 2px 6px rgba(44, 52, 123, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": {
                      background: "#e0e0e0",
                      color: "#999",
                      boxShadow: "none",
                      transform: "none",
                    },
                  }}
                >
                  Save As
                </Button>
              )}
              </Box>
              <Box ref={actionsMenuContainerRef} sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title="Actions" placement="bottom">
                  <IconButton
                    onClick={handleActionsMenuOpen}
                    sx={{
                      background: "rgb(44, 52, 123)",
                      color: "#fff",
                      width: 32,
                      height: 32,
                      "&:hover": {
                        background: "rgba(44, 52, 123, 0.85)",
                        boxShadow: "0 2px 6px rgba(44, 52, 123, 0.3)",
                      },
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={actionsMenuAnchor}
                  open={actionsMenuOpen}
                  onClose={handleActionsMenuClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  disablePortal
                  container={actionsMenuContainerRef.current}
                  slotProps={{ paper: { sx: { minWidth: 220, mt: 1.5 } } }}
                >
                  {queryResultTableData && queryResultTableData.length > 0 && (
                    <MenuItem
                      onClick={() => {
                        const xVal = chartAxisOptions[0] || "";
                        const yVal = chartAxisOptions[1] || chartAxisOptions[0] || "";
                        setChartXAxis(xVal);
                        setChartYAxis(yVal);
                        setChartXAxisDateDetection(null);
                        setChartYAxisDateDetection(null);
                        setChartData(null);
                        setChartError(null);
                        setChartDialogOpen(true);
                        handleActionsMenuClose();
                        if (xVal && generatedSqlQuery?.trim() && userId) {
                          fetchColumnDateDetection(xVal, setChartXAxisDateDetection);
                        }
                        if (yVal && generatedSqlQuery?.trim() && userId) {
                          fetchColumnDateDetection(yVal, setChartYAxisDateDetection);
                        }
                      }}
                    >
                      <ListItemIcon>
                        <BarChartIcon sx={{ fontSize: 18 }} />
                      </ListItemIcon>
                      Chart
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={async () => {
                      if (!generatedSqlQuery || !generatedSqlQuery.trim()) {
                        handleActionsMenuClose();
                        return;
                      }
                      setUnderlinedDownloadLoading(true);
                      handleActionsMenuClose();
                      try {
                        await downloadQueryAsync(
                          {
                            sql: generatedSqlQuery,
                            tabName:
                              sheets?.[activeSheetIndex]?.name ||
                              queryName ||
                              `Task-${activeSheetIndex + 1}`,
                          },
                          "plain",
                          "csv"
                        );
                      } finally {
                        setUnderlinedDownloadLoading(false);
                      }
                    }}
                    disabled={
                      !generatedSqlQuery ||
                      !generatedSqlQuery.trim() ||
                      !queryResultTableData ||
                      queryResultTableData.length === 0 ||
                      underlinedDownloadLoading
                    }
                  >
                    <ListItemIcon>
                      <DownloadIcon sx={{ fontSize: 18 }} />
                    </ListItemIcon>
                    Plain CSV
                    {underlinedDownloadLoading && (
                      <CircularProgress size={14} sx={{ ml: 1 }} />
                    )}
                  </MenuItem>
                  <MenuItem
                    onClick={async () => {
                      if (!generatedSqlQuery || !generatedSqlQuery.trim()) {
                        handleActionsMenuClose();
                        return;
                      }
                      setSummarizedDownloadLoading(true);
                      handleActionsMenuClose();
                      try {
                        await downloadQueryAsync(
                          {
                            sql: generatedSqlQuery,
                            tabName:
                              sheets?.[activeSheetIndex]?.name ||
                              queryName ||
                              `Task-${activeSheetIndex + 1}`,
                          },
                          "pivot",
                          "csv"
                        );
                      } finally {
                        setSummarizedDownloadLoading(false);
                      }
                    }}
                    disabled={
                      !generatedSqlQuery ||
                      !generatedSqlQuery.trim() ||
                      !queryResultTableData ||
                      queryResultTableData.length === 0 ||
                      summarizedDownloadLoading
                    }
                  >
                    <ListItemIcon>
                      <DownloadIcon sx={{ fontSize: 18 }} />
                    </ListItemIcon>
                    Pivot CSV
                    {summarizedDownloadLoading && (
                      <CircularProgress size={14} sx={{ ml: 1 }} />
                    )}
                  </MenuItem>
                  {((columns && columns.length > 0) ||
                    (rows && rows.length > 0) ||
                    (data && data.length > 0) ||
                    (filters && filters.length > 0)) &&
                    allDbnamesMatch && (
                      <MenuItem
                        onClick={() => {
                          setEditColumnName(null);
                          setEditFormula(null);
                          setFailedCalculatedColumn(null);
                          setSqlGenError("");
                          setAddColumnModalOpen(true);
                          handleActionsMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <AddIcon sx={{ fontSize: 18 }} />
                        </ListItemIcon>
                        Add Calculated Column
                      </MenuItem>
                    )}
                </Menu>
              </Box>
            </Box>

            {/* Table Section - Takes remaining space, maxHeight so scrollbars always have a viewport */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                width: "100%",
                minHeight: 0,
                maxHeight: "100vh",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {queryResultTableData && queryResultTableData.length > 0 ? (
                  // Show table when data exists
                  <>
                    <DynamicNestedTable
                      tableData={queryResultTableData || []}
                      tableLoading={false}
                      expandedMap={expandedMap}
                      onExpand={handleExpand}
                      headers={
                        Array.isArray(queryResultTableData) &&
                          queryResultTableData.length > 0
                          ? Array.from(
                            new Set(
                              queryResultTableData.flatMap((row) => Object.keys(row))
                            )
                          )
                          : undefined
                      }
                      computedColumns={computedColumns}
                      onDeleteComputedColumn={removeComputedColumn}
                      onEditComputedColumn={(columnName) => {
                        const computedCol = computedColumns.find(cc => cc.columnName === columnName);
                        if (computedCol) {
                          setEditColumnName(computedCol.columnName);
                          setEditFormula(computedCol.formula);
                          setAddColumnModalOpen(true);
                        }
                      }}
                    />
                  </>
                ) : (
                  // Show "No data" message with action prompt
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      p: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "text.secondary",
                        gap: 3,
                        minHeight: "calc(100vh - 350px)",
                      }}
                    >
                      <TableChartOutlinedIcon
                        sx={{ fontSize: 56, color: "rgb(44, 52, 123)", opacity: 0.2 }}
                      />
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: 16,
                            color: "#1a1a1a",
                            mb: 1,
                          }}
                        >
                          No Table Data to Display
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: "#666",
                            maxWidth: 400,
                          }}
                        >
                          Configure your query parameters using the options above
                          and click "Run Query" to see the results
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        onClick={handleGenerateSQL}
                        startIcon={<BoltIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          background: "rgb(44, 52, 123)",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: 13,
                          borderRadius: 2,
                          px: 3,
                          py: 1,
                          textTransform: "none",
                          "&:hover": {
                            background: "rgba(44, 52, 123, 0.8)",
                          },
                        }}
                      >
                        Run Query
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>
      <Dialog
        open={uploadDialogOpen}
        onClose={handleCloseUploadDialog}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 0,
            m: 0,
            bgcolor: "transparent",
          }}
        >
          <Box
            sx={{
              border: "2.5px dashed rgb(44, 52, 123)",
              borderRadius: "16px",
              boxShadow: "0 2px 8px 0 rgba(63,73,167,0.08)",
              background: "#fff",
              maxWidth: 420,
              width: "100%",
              mx: "auto",
              mt: 2,
              mb: 2,
              p: 2.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CloudUploadIcon
              sx={{
                fontSize: 48,
                color: "rgb(44, 52, 123)",
                mb: 1.5,
                bgcolor: "#e9f1fb",
                borderRadius: "50%",
                p: 0.8,
              }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 20,
                mb: 0.8,
                color: "#111",
                textAlign: "center",
              }}
            >
              Drag and Drop files to upload
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                mb: 1.5,
                textAlign: "center",
                fontSize: 15,
              }}
            >
              or
            </Typography>
            <Button
              variant="contained"
              sx={{
                mb: 1.5,
                fontSize: 15,
                fontWeight: 600,
                background: "rgb(44, 52, 123)",
                color: "#fff",
                borderRadius: 2,
                px: 3,
                py: 1,
                boxShadow: "none",
                textTransform: "none",
                "&:hover": {
                  background: "rgba(44, 52, 123, 0.8)",
                },
              }}
              component="label"
            >
              Browse
              <input
                type="file"
                multiple
                accept=".xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            <Typography
              sx={{
                color: "#bdbdbd",
                fontSize: 13,
                mb: 1,
                mt: 0.5,
                textAlign: "center",
              }}
            >
              Supported files: .xlsx, .csv
            </Typography>
          </Box>
          <Box sx={{ width: "100%", maxWidth: 420, mx: "auto", px: 0, pb: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
                px: 2,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#111" }}>
                Select the sheet to upload
              </Typography>
            </Box>
            {uploadedFiles.map((file, idx) => (
              <Box
                key={file.name + idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "#f8fafd",
                  borderRadius: 3,
                  p: 1.2,
                  mb: 1,
                  mx: 2,
                  boxShadow: "none",
                }}
              >
                {file.icon}
                <Typography
                  sx={{
                    fontSize: 15,
                    ml: 1,
                    color: "rgb(44, 52, 123)",
                    fontWeight: 500,
                  }}
                >
                  {file.name}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
              maxWidth: 420,
              mx: "auto",
              px: 2,
              pb: 2,
            }}
          >
            <Button
              onClick={handleCloseUploadDialog}
              color="inherit"
              sx={{ fontWeight: 500, fontSize: 15 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* SQL Editor Component */}
      <SqlEditor
        sqlQuery={generatedSqlQuery}
        onSqlChange={setGeneratedSqlQuery}
        onExecuteQuery={executeCustomSql}
        isExecuting={runQueryLoading}
      />
      {/* Show error modal if SQL generation error occurs */}
      {isModalOpen && (
        <Modal
          type="error"
          title="SQL Generation Error"
          message={sqlGenError}
          onClose={handleModalClose}
          onAction={failedCalculatedColumn ? handleTryAgain : null}
        />
      )}

      {/* Save Query Dialog */}
      <Dialog
        open={saveQueryDialogOpen}
        onClose={closeSaveQueryDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxWidth: 400,
          },
        }}
      >
        {/* Compact Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, rgb(44, 52, 123) 0%, rgba(44, 52, 123, 0.8) 100%)",
            color: "white",
            p: 1.2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              background: "rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SaveIcon sx={{ fontSize: 14, color: "white" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
                color: "white",
                mb: 0.1,
              }}
            >
              Save Query
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: "rgba(255, 255, 255, 0.8)",
                fontWeight: 400,
              }}
            >
              {formattedSqlQuery
                ? `${formattedSqlQuery.split("\n").length} lines`
                : "No query"}
            </Typography>
          </Box>
        </Box>

        {/* Compact Content */}
        <Box sx={{ p: 2 }}>
          {/* Mini Query Preview */}
          <Box
            sx={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 1.5,
              p: 1.2,
              mb: 1.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                color: "#64748b",
                fontWeight: 600,
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Preview
            </Typography>
            <Box
              sx={{
                background: "#1e293b",
                color: "#e2e8f0",
                borderRadius: 1,
                p: 1.2,
                fontFamily:
                  "'JetBrains Mono', 'Fira Code', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                fontSize: 11,
                maxHeight: 80,
                overflow: "auto",
                border: "1px solid #334155",
                lineHeight: 1.4,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {formattedSqlQuery?.substring(0, 150) || "No SQL query available"}
              {formattedSqlQuery?.length > 150 && "..."}
            </Box>
          </Box>

          {/* Compact Input Field */}
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                Query Name *
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  color: queryName.length > 50 ? "#ef4444" : "#9ca3af",
                  fontWeight: 500,
                }}
              >
                {queryName.length}/50
              </Typography>
            </Box>
            <InputBase
              placeholder="Enter query name..."
              value={queryName}
              onChange={(e) => {
                const raw = e.target.value || "";
                if (!QUERY_NAME_ALLOWED_RE.test(raw)) return;
                if (raw.length > QUERY_NAME_MAX_LEN) return;
                setQueryName(raw);
              }}
              onKeyDown={(e) => {
                // Allow navigation/editing keys + shortcuts
                if (e.ctrlKey || e.metaKey || e.altKey) return;
                if (e.key.length !== 1) return;
                if (!QUERY_NAME_ALLOWED_RE.test(e.key)) {
                  e.preventDefault();
                  return;
                }
                if ((queryName || "").length >= QUERY_NAME_MAX_LEN) {
                  // Prevent adding more characters past the limit
                  e.preventDefault();
                }
              }}
              onPaste={(e) => {
                const pasted = e.clipboardData?.getData("text") ?? "";
                const next = `${queryName || ""}${pasted}`;
                if (!QUERY_NAME_ALLOWED_RE.test(next) || next.length > QUERY_NAME_MAX_LEN) {
                  e.preventDefault();
                }
              }}
              inputProps={{
                maxLength: QUERY_NAME_MAX_LEN,
                pattern: "[A-Za-z0-9_]*",
              }}
              autoFocus
              sx={{
                border: `1.5px solid ${queryName.length >= 50 ? "#fca5a5" : "#e5e7eb"}`,
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
                fontSize: 13,
                fontWeight: 500,
                width: "100%",
                transition: "all 0.2s ease",
                "&:focus": {
                  borderColor: queryName.length >= 50 ? "#ef4444" : "rgb(44, 52, 123)",
                  boxShadow: `0 0 0 2px ${queryName.length >= 50 ? "rgba(239, 68, 68, 0.1)" : "rgba(44, 52, 123, 0.1)"}`,
                  outline: "none",
                },
                "&:hover": {
                  borderColor: queryName.length >= 50 ? "#fca5a5" : "#d1d5db",
                },
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter" && queryName.trim() && queryName.length <= 50) {
                  saveQuery();
                }
              }}
            />
          </Box>

          {/* Compact Success Message */}
          {saveQuerySuccess && (
            <Box
              sx={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 1.5,
                p: 1.5,
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: "#22c55e",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9,
                    color: "white",
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  ✓
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#16a34a",
                  fontWeight: 500,
                }}
              >
                Query saved successfully!
              </Typography>
            </Box>
          )}

          {/* Compact Error Message */}
          {saveQueryError && (
            <Box
              sx={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 1.5,
                p: 1.5,
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9,
                    color: "white",
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  !
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#dc2626",
                  fontWeight: 500,
                }}
              >
                {saveQueryError}
              </Typography>
            </Box>
          )}

          {/* Compact Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              onClick={closeSaveQueryDialog}
              variant="outlined"
              size="small"
              sx={{
                borderColor: "#d1d5db",
                color: "#6b7280",
                fontWeight: 500,
                fontSize: 13,
                borderRadius: 1.5,
                px: 2,
                py: 0.8,
                textTransform: "none",
                minWidth: 70,
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f9fafb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={saveQuery}
              disabled={!queryName.trim() || queryName.length > 50}
              size="small"
              startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
              sx={{
                background: "rgb(44, 52, 123)",
                color: "white",
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 1.5,
                px: 2,
                py: 0.8,
                textTransform: "none",
                minWidth: 90,
                boxShadow: "0 2px 6px rgba(44, 52, 123, 0.2)",
                "&:hover": {
                  background:
                    "rgba(44, 52, 123, 0.8)",
                  boxShadow: "0 3px 8px rgba(44, 52, 123, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:disabled": {
                  background: "#f3f4f6",
                  color: "#9ca3af",
                  boxShadow: "none",
                  transform: "none",
                },
                transition: "all 0.2s ease",
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Chart options dialog – enhanced UI */}
      <Dialog
        open={chartDialogOpen}
        onClose={() => {
          setChartDialogOpen(false);
          setChartData(null);
          setChartXAxisDateDetection(null);
          setChartYAxisDateDetection(null);
          setChartError(null);
        }}
        maxWidth={chartData ? "md" : "sm"}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            overflow: "hidden",
            border: "1px solid #E9EAF7",
            boxShadow: "0 32px 64px -12px rgba(1, 2, 10, 0.12), 0 12px 24px -8px rgba(1, 2, 10, 0.06)",
            backgroundColor: "#FFFFFF",
          },
        }}
      >
        <Box sx={{ height: 3, background: "linear-gradient(90deg, #3F49A7 0%, #5F80F6 100%)" }} />
        <Box
          sx={{
            p: 2,
            backgroundColor: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: "linear-gradient(135deg, #EBEDF7 0%, #E9EAF7 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #E9EAF7",
                boxShadow: "0 1px 2px rgba(1, 2, 10, 0.04)",
              }}
            >
              <BarChartIcon sx={{ fontSize: 20, color: "#3F49A7" }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#01020A", letterSpacing: "-0.02em", lineHeight: 1.2 }}>
                Chart options
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#909193", mt: 0.25 }}>
                Pick axes and apply to visualize
              </Typography>
            </Box>
          </Box>

          <Box sx={{ width: "100%" }}>
            {isChartXAxisDateType && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11, color: "#525458", mb: 0.5, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                    Year
                  </Typography>
                  <Dropdown
                    options={chartYearOptions.map((y) => ({ value: y, label: String(y) }))}
                    value={chartYear}
                    onChange={(v) => setChartYear(v != null ? Number(v) : new Date().getFullYear())}
                    placeholder="Year"
                    searchable={false}
                    label=""
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11, color: "#525458", mb: 0.5, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                    Period
                  </Typography>
                  <Dropdown
                    options={[
                      { value: "monthly", label: "Monthly" },
                      { value: "quarterly", label: "Quarterly" },
                      { value: "weekly", label: "Weekly" },
                      { value: "yearly", label: "Yearly" },
                    ]}
                    value={chartPeriod}
                    onChange={(v) => setChartPeriod(v || "monthly")}
                    placeholder="Period"
                    searchable={false}
                    label=""
                  />
                </Box>
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 11, color: "#525458", mb: 0.5, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                  X-axis
                </Typography>
                <Dropdown
                  options={chartAxisOptions.map((h) => ({ value: h, label: h }))}
                  value={chartXAxis || null}
                  onChange={(v) => {
                    const val = v ?? "";
                    setChartXAxis(val);
                    fetchColumnDateDetection(val, setChartXAxisDateDetection);
                  }}
                  placeholder="X-axis"
                  searchable={chartAxisOptions.length > 8}
                  label=""
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 11, color: "#525458", mb: 0.5, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                  Y-axis
                </Typography>
                <Dropdown
                  options={chartAxisOptions.map((h) => ({ value: h, label: h }))}
                  value={chartYAxis || null}
                  onChange={(v) => {
                    const val = v ?? "";
                    setChartYAxis(val);
                    fetchColumnDateDetection(val, setChartYAxisDateDetection);
                  }}
                  placeholder="Y-axis"
                  searchable={chartAxisOptions.length > 8}
                  label=""
                />
              </Box>
            </Box>

            {chartError && (
              <Alert
                severity="error"
                onClose={() => setChartError(null)}
                sx={{
                  mb: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: "#EBEDF7",
                  border: "1px solid #D2DAF7",
                  "& .MuiAlert-message": { color: "#01020A", fontSize: 12 },
                }}
              >
                {chartError}
              </Alert>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1.25,
                mt: 2,
                pt: 2,
                borderTop: "1px solid #E9EAF7",
                width: "100%",
              }}
            >
            <Button
              variant="outlined"
              onClick={() => {
                setChartDialogOpen(false);
                setChartXAxisDateDetection(null);
                setChartYAxisDateDetection(null);
              }}
              sx={{
                color: "#525458",
                borderColor: "#C0C4E7",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                px: 2.25,
                py: 0.875,
                borderRadius: 1.5,
                minHeight: 40,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#3F49A7",
                  color: "#3F49A7",
                  backgroundColor: "rgba(63, 73, 167, 0.06)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={!getChartDataLoading ? <CheckIcon sx={{ fontSize: 18 }} /> : null}
              disabled={
                !chartXAxis ||
                !chartYAxis ||
                !generatedSqlQuery?.trim() ||
                getChartDataLoading
              }
              onClick={async () => {
                setChartError(null);
                let userDbId =
                  StorageHelper.get("snowflake_datasource_id") ||
                  StorageHelper.get("selected_user_db_id");
                const currentSheet = sheets?.[activeSheetIndex];
                const allTablesAreUploaded =
                  currentSheet?.data?.columns?.every((c) =>
                    c?.dbname?.startsWith?.("temp_")
                  ) || false;
                if (allTablesAreUploaded) userDbId = "0";
                const dbType =
                  currentSheet?.data?.dbtype ||
                  getCurrentDbType() ||
                  "snowflake";
                const columnFields = (columns || []).map((c) => c.name).filter(Boolean);
                const rowFields = (rows || []).map((r) => r.name).filter(Boolean);
                const dataFields = (data || []).map((d) => d.name).filter(Boolean);
                const result = await getChartDataApi(
                  { sql: generatedSqlQuery },
                  Number(userId),
                  Number(userDbId),
                  dbType,
                  isChartXAxisDateType ? chartPeriod : "category",
                  isChartXAxisDateType ? chartYear : null,
                  chartXAxis,
                  chartYAxis,
                  columnFields,
                  rowFields,
                  dataFields
                );
                if (result.success && result.data) {
                  setChartData(result.data);
                } else {
                  setChartData(null);
                  setChartError(
                    result.error ||
                      result.message ||
                      "Failed to load chart data"
                  );
                }
              }}
              sx={{
                background: "linear-gradient(180deg, #5F80F6 0%, #3F49A7 100%)",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                px: 2.25,
                py: 0.875,
                borderRadius: 1.5,
                minHeight: 40,
                boxShadow: "0 2px 8px rgba(63, 73, 167, 0.3)",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "linear-gradient(180deg, #6B8BF7 0%, #345FF3 100%)",
                  boxShadow: "0 4px 12px rgba(63, 73, 167, 0.4)",
                },
                "&:disabled": {
                  background: "#C0C4E7",
                  color: "#909193",
                  boxShadow: "none",
                },
              }}
            >
              {getChartDataLoading ? (
                <>
                  <CircularProgress size={18} sx={{ color: "#fff", mr: 0.75 }} />
                  Loading…
                </>
              ) : (
                "Apply"
              )}
            </Button>
          </Box>
          </Box>

          {chartData && chartData.labels && chartData.values && (
            <Box
              sx={{
                mt: 2,
                width: "100%",
                alignSelf: "stretch",
                borderRadius: 2,
                background: "linear-gradient(180deg, #FFFFFF 0%, #FAFBFF 50%, #F5F7FF 100%)",
                border: "1px solid #E9EAF7",
                boxShadow: "0 4px 20px rgba(1, 2, 10, 0.06), 0 1px 3px rgba(63, 73, 167, 0.08), inset 0 1px 0 0 rgba(255,255,255,0.8)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1.5,
                  borderBottom: "1px solid #E9EAF7",
                  backgroundColor: "rgba(255,255,255,0.6)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, backgroundColor: "#3F49A7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <BarChartIcon sx={{ fontSize: 16, color: "#fff" }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#01020A", letterSpacing: "-0.02em" }}>
                    Chart
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 0,
                    borderRadius: 1.5,
                    border: "1px solid #C0C4E7",
                    overflow: "hidden",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0 1px 2px rgba(1, 2, 10, 0.04)",
                  }}
                >
                  {[
                    { type: "bar", label: "Bar", Icon: BarChartIcon },
                    { type: "line", label: "Line", Icon: ShowChartIcon },
                    { type: "area", label: "Area", Icon: TimelineIcon },
                  ].map(({ type, label, Icon }) => (
                    <Tooltip key={type} title={label}>
                      <IconButton
                        size="small"
                        onClick={() => setChartType(type)}
                        sx={{
                          borderRadius: 0,
                          width: 44,
                          height: 38,
                          bgcolor: chartType === type ? "#3F49A7" : "transparent",
                          color: chartType === type ? "#fff" : "#525458",
                          "&:hover": {
                            bgcolor: chartType === type ? "#345FF3" : "#EBEDF7",
                            color: chartType === type ? "#fff" : "#01020A",
                          },
                          "&:not(:last-child)": { borderRight: "1px solid #E9EAF7" },
                        }}
                      >
                        <Icon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
              <Box sx={{ height: 340, width: "100%" }}>
                <ResponsiveContainer width="100%" height="100%">
                  {chartType === "bar" && (
                    <BarChart
                      data={chartData.labels.slice(0, 12).map((label, i) => ({
                        name: String(label),
                        value: Number(chartData.values[i]) || 0,
                      }))}
                      margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                      barCategoryGap="18%"
                      barGap={4}
                    >
                      <defs>
                        <linearGradient id="chartBarGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#5F80F6" stopOpacity={1} />
                          <stop offset="50%" stopColor="#345FF3" stopOpacity={1} />
                          <stop offset="100%" stopColor="#3F49A7" stopOpacity={1} />
                        </linearGradient>
                        <linearGradient id="chartBarHover" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#A1B4F7" stopOpacity={1} />
                          <stop offset="100%" stopColor="#6069BB" stopOpacity={1} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E9EAF7" vertical={false} strokeWidth={1} />
                      <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#525458", fontWeight: 500 }} axisLine={{ stroke: "#C0C4E7", strokeWidth: 1 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: "#525458", fontWeight: 500 }} axisLine={false} tickLine={{ stroke: "#E9EAF7" }} tickFormatter={(v) => (v >= 1e9 ? `${(v / 1e9).toFixed(1)}B` : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : v)} />
                      <RechartsTooltip
                        formatter={(value) => [typeof value === "number" ? value.toLocaleString() : value, chartYAxis]}
                        labelFormatter={(label) => `${chartXAxis}: ${label}`}
                        contentStyle={{
                          borderRadius: 10,
                          border: "1px solid #C0C4E7",
                          boxShadow: "0 8px 24px rgba(1, 2, 10, 0.1)",
                          padding: "12px 16px",
                          backgroundColor: "#FFFFFF",
                          fontWeight: 500,
                        }}
                        labelStyle={{ color: "#01020A", fontWeight: 600 }}
                        cursor={{ fill: "rgba(63, 73, 167, 0.08)" }}
                      />
                      <Bar
                        dataKey="value"
                        fill="url(#chartBarGradient)"
                        radius={[8, 8, 0, 0]}
                        maxBarSize={52}
                        isAnimationActive
                        animationDuration={1400}
                        animationEasing="ease-out"
                        animationBegin={(index) => index * 70}
                        activeBar={{ fill: "url(#chartBarHover)", stroke: "#3F49A7", strokeWidth: 1.5 }}
                      >
                        {chartData.labels.slice(0, 12).map((_, index) => (
                          <Cell key={`cell-${index}`} fill="url(#chartBarGradient)" />
                        ))}
                      </Bar>
                    </BarChart>
                  )}
                  {chartType === "line" && (
                    <LineChart
                      data={chartData.labels.slice(0, 12).map((label, i) => ({
                        name: String(label),
                        value: Number(chartData.values[i]) || 0,
                      }))}
                      margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                    >
                      <defs>
                        <linearGradient id="chartLineStroke" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#3F49A7" />
                          <stop offset="100%" stopColor="#5F80F6" />
                        </linearGradient>
                        <linearGradient id="chartLineAreaFill" x1="0" y1="1" x2="0" y2="0">
                          <stop offset="0%" stopColor="#5F80F6" stopOpacity={0.2} />
                          <stop offset="50%" stopColor="#3F49A7" stopOpacity={0.12} />
                          <stop offset="100%" stopColor="#A1B4F7" stopOpacity={0.06} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E9EAF7" vertical={false} strokeWidth={1} />
                      <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#525458", fontWeight: 500 }} axisLine={{ stroke: "#C0C4E7", strokeWidth: 1 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: "#525458", fontWeight: 500 }} axisLine={false} tickLine={{ stroke: "#E9EAF7" }} tickFormatter={(v) => (v >= 1e9 ? `${(v / 1e9).toFixed(1)}B` : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : v)} />
                      <RechartsTooltip
                        formatter={(value) => [typeof value === "number" ? value.toLocaleString() : value, chartYAxis]}
                        labelFormatter={(label) => `${chartXAxis}: ${label}`}
                        contentStyle={{
                          borderRadius: 10,
                          border: "1px solid #C0C4E7",
                          boxShadow: "0 8px 24px rgba(1, 2, 10, 0.1)",
                          padding: "12px 16px",
                          backgroundColor: "#FFFFFF",
                          fontWeight: 500,
                        }}
                        labelStyle={{ color: "#01020A", fontWeight: 600 }}
                        cursor={{ stroke: "#3F49A7", strokeWidth: 1, strokeDasharray: "4 4" }}
                      />
                      <Area type="monotone" dataKey="value" fill="url(#chartLineAreaFill)" stroke="none" isAnimationActive animationDuration={1400} animationEasing="ease-out" />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="url(#chartLineStroke)"
                        strokeWidth={3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        dot={{ fill: "#FFFFFF", stroke: "#5F80F6", strokeWidth: 2, r: 5 }}
                        activeDot={{ r: 8, fill: "#5F80F6", stroke: "#3F49A7", strokeWidth: 2.5 }}
                        isAnimationActive
                        animationDuration={1400}
                        animationEasing="ease-out"
                        animationBegin={200}
                      />
                    </LineChart>
                  )}
                  {chartType === "area" && (
                    <AreaChart
                      data={chartData.labels.slice(0, 12).map((label, i) => ({
                        name: String(label),
                        value: Number(chartData.values[i]) || 0,
                      }))}
                      margin={{ top: 16, right: 16, left: 16, bottom: 16 }}
                    >
                      <defs>
                        <linearGradient id="chartAreaGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#5F80F6" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#3F49A7" stopOpacity={0.08} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#D2DAF7" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#525458" }} axisLine={{ stroke: "#C0C4E7" }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "#525458" }} axisLine={false} tickLine={{ stroke: "#C0C4E7" }} tickFormatter={(v) => (v >= 1e9 ? `${(v / 1e9).toFixed(1)}B` : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : v)} />
                      <RechartsTooltip formatter={(value) => [typeof value === "number" ? value.toLocaleString() : value, chartYAxis]} labelFormatter={(label) => `${chartXAxis}: ${label}`} contentStyle={{ borderRadius: 8, border: "1px solid #C0C4E7", boxShadow: "0 4px 12px rgba(1, 2, 10, 0.06)", padding: "10px 14px", backgroundColor: "#FFFFFF" }} cursor={{ stroke: "#C0C4E7", strokeWidth: 1 }} />
                      <Area type="monotone" dataKey="value" stroke="#3F49A7" strokeWidth={2} fill="url(#chartAreaGradient)" isAnimationActive animationDuration={1200} animationEasing="ease-out" />
                    </AreaChart>
                  )}
                </ResponsiveContainer>
              </Box>
            </Box>
          )}
        </Box>
      </Dialog>

      {modal.open && (
        <Modal
          type={modal.type}
          title={modal.title}
          message={modal.message}
          onClose={() => setModal({ ...modal, open: false })}
        />
      )}

      {/* Add Column Modal */}
      <AddColumnModal
        open={addColumnModalOpen}
        onClose={() => {
          setAddColumnModalOpen(false);
          setEditColumnName(null);
          setEditFormula(null);
          // Clear failed column state and SQL error when modal closes
          setFailedCalculatedColumn(null);
          setSqlGenError("");
        }}
        onAddColumn={addComputedColumn}
        onUpdateColumn={(oldColumnName, newColumnName, newFormula) => {
          // If updating a failed calculated column, treat it as adding a new column
          if (failedCalculatedColumn && failedCalculatedColumn.columnName === oldColumnName) {
            addComputedColumn(
              newColumnName,
              newFormula,
              failedCalculatedColumn.tableName,
              failedCalculatedColumn.dataType || failedCalculatedColumn.datatype || failedCalculatedColumn.type || null
            );
          } else {
            // Otherwise, update the existing column
            updateComputedColumn(oldColumnName, newColumnName, newFormula);
          }
        }}
        onDeleteColumn={async (columnName) => {

          // Close the modal and clear error states
          setAddColumnModalOpen(false);
          setEditColumnName(null);
          setEditFormula(null);
          setFailedCalculatedColumn(null);
          setSqlGenError("");

          // SQL is only regenerated when user clicks "Run Query" button, not on delete
        }}
        editMode={!!editColumnName}
        editColumnName={editColumnName}
        editFormula={editFormula}
        isPostgres={isPostgresSheet}
        defaultTableName={commonTableName}
        existingComputedColumns={computedColumns}
        sqlError={failedCalculatedColumn?.errorMessage || null}
      />
    </Box>
  );
}
